import * as React from "react";
import * as Scrivito from "scrivito";
import cx from "classnames";
import { UncontrolledTooltip } from "reactstrap";
import { Icon } from "components";
import {
  useProductsOverviewActions,
  useProductsOverviewStore,
} from "stores/productsOverviewStore";
import s from "./ProductListLeftSidebar.module.scss";
import { useProject } from "stores/project";
import type { ProductNode } from "interfaces/product/product.types";

type ProductsNavigationProps = {
  product: ProductNode;
};

const isScrivitoObjLink = (
  link: ProductNode["link"],
): link is Scrivito.Obj & { get: (attribute: string) => unknown } => {
  return !!link && typeof link === "object" && "get" in link;
};

export const ProductsNavigation = Scrivito.connect(
  ({ product }: ProductsNavigationProps) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const tooltipId =
      (product.name || "").replace(/[^a-zA-Z0-9]/g, "") || "defaultTooltip";

    const selectedCategory = useProductsOverviewStore(
      (state) => state.productCategoryId,
    );

    React.useEffect(() => {
      setIsOpen(selectedCategory.includes(product?.key));
    }, [product, selectedCategory]);

    const {
      setProductCategoryId,
      setProductsContent,
      navigateToConfiguratorPage,
      navigateToProductListPage,
      markSidebarChange,
    } = useProductsOverviewActions();

    const project = useProject();

    const isCurrentCategory =
      !!product?.key &&
      selectedCategory[selectedCategory.length - 1] === product.key;

    const selectCategory = React.useCallback(() => {
      if (isCurrentCategory) {
        return;
      }

      if (isScrivitoObjLink(product.link)) {
        const maybeLink = product.link.get("linkForMenuItem") as
          | Scrivito.Link
          | null
          | undefined;
        const url = maybeLink?.url?.();
        if (url) {
          window.open(url, "_blank");
        }
      } else if (product.nodes && selectedCategory !== product.keyList) {
        setProductsContent(product.nodes);
        setProductCategoryId(product.keyList ?? []);
        navigateToProductListPage({
          projectId: project?.id,
          shouldKeepProductCategories: true,
        });
      } else {
        setProductCategoryId(product.keyList ?? []);

        navigateToConfiguratorPage({
          projectId: project?.id,
          seriesId: `${product.serieId}`,
          shouldKeepProductCategories: true,
        });
        markSidebarChange();
      }
    }, [
      isCurrentCategory,
      product.link,
      product.nodes,
      product.keyList,
      product.serieId,
      selectedCategory,
      setProductsContent,
      setProductCategoryId,
      navigateToProductListPage,
      project?.id,
      navigateToConfiguratorPage,
      markSidebarChange,
    ]);

    const toggleChildren = () => setIsOpen((prev) => !prev);

    return (
      <div className={cx(s.productCategory, "cursor-pointer h-auto ms-3")}>
        <div
          className={cx(
            s.productTitle,
            {
              [s.active]: selectedCategory?.includes(product?.key),
            },
            "py-2 ps-2   d-flex align-items-center justify-content-between",
          )}
        >
          <button
            id={tooltipId}
            className={cx(
              s.titleDefault,
              {
                "cursor-default": isCurrentCategory,
              },
              "me-2 p-0 h-100 w-100 text-truncate flex-grow-1 bg-transparent border-0 text-start text-white",
            )}
            onClick={selectCategory}
            disabled={isCurrentCategory}
          >
            {product.name}
          </button>

          <UncontrolledTooltip target={tooltipId} placement="top">
            {product.name}
          </UncontrolledTooltip>

          {product.nodes && (
            <button
              className="d-flex h-100 align-items-center justify-content-center mx-1 px-1 py-0 bg-transparent border-0 text-white"
              onClick={toggleChildren}
            >
              <Icon
                iconName={
                  isOpen ? "chevron-up-regular" : "chevron-down-regular"
                }
              />
            </button>
          )}
        </div>
        {isOpen &&
          product?.nodes?.map((childProduct, index) => (
            <ProductsNavigation
              key={childProduct.key ?? `${product.key}-${index}`}
              product={childProduct}
            />
          ))}
      </div>
    );
  },
);
